/*
* Component base class for components that use messages & t('token')
* Note: all derived classes must initialize 'messagesPathPrefix'!
*/

import * as React from 'react';
import * as _ from 'lodash';
import LoadingBar from 'react-redux-loading-bar';
import { RouteComponentProps } from 'react-router-dom';
import { Row } from 'react-bootstrap';

import Footer from '../footer';
import Nav from '../applayout/nav';
import SidebarNavigation from '../applayout/sidebar-navigation';
import { BootstrapColXl as Col } from '../common/bootstrap/bootstrapcolxl';
import { Formatters } from '../common/formatters';
import Auth from '../auth/auth';

import '../applayout/sidebar-nav.css';
import '../applayout/animate.css';
import '../applayout/style.css';
import '../applayout/colors/default.css';

// Common component/page props
export interface IAppComponentProps extends RouteComponentProps<{}> {
    branding?: any;
    children?: any;
    disableAppLayoutWrapper?: boolean;
    dispatch?: any;
    history: any;
    intl?: any;
    location: any;
    lookups?: any;
    match: any;
    props?: any;
    pageName?: any;
    authsession?: any;
    search?: any;
    authUser?: any;
}

export interface IAppComponentState extends Partial<object> { }

export const RenderPageWithLayout = ({ pageName, children, wrapperClassName = '', ...rest }) => {

    return (
        <div id="wrapper" className={`app-layout-wrapper ${wrapperClassName}`}>
            <Nav {...rest} />
            <SidebarNavigation {...rest} />

            <div id="page-wrapper" className="app-layout-page-wrapper">
                <LoadingBar showFastActions={true} progressIncrease={3} className="loadingBar" />
                <div className="container-fluid">
                    <Row className="bg-title">
                        <Col xs={12}><h4 className="page-title">{pageName}</h4></Col>
                    </Row>

                    {children}
                </div>

                <Footer {...rest} />
            </div>
        </div>
    );
};

// TODO: REFACTOR APPTEXT TO OWN CLASS

// TODO: REFACTOR AWAY AppComponentNoWrapper

export class AppComponent<TProps extends IAppComponentProps = IAppComponentProps, TState = IAppComponentState> extends React.Component<TProps & RouteComponentProps<{}>, TState> {
    public messagesPathPrefix: string = '';

    public readonly commonMessagePaths = {
        dateInputPlaceholder: 'app.common.forms.date-input.placeholder',
        filterOptionAny: 'app.common.forms.filter-option-any',
        gridColumnHeaders: 'grid.column-headers.',
        numberInputPlaceholder: 'app.common.forms.number-input.placeholder',
        pageTitle: 'page-title',
        searchResults: 'app.common.search.results',
        searchResult: 'app.common.search.result',
        selectOptionPlaceholder: 'app.common.forms.select-option.placeholder',
    };

    public constructor(p: TProps, messagesPathPrefix: string = '') {
        super(p);

        if (messagesPathPrefix) { this.messagesPathPrefix = messagesPathPrefix; }

        // console.log(`AppComponent constructor messagepath ${messagesPathPrefix} props`, p);

    }

    /* Children cloning & copy props */
    public cloneChildrenWithProps = (children, props) => {
        return (React.cloneElement(children as React.ReactElement<any>, { ...props }));
    }

    /* Branding/Internationalization/App Text functionality */
    public appText(messagePath: string, prependMessagesPathPrefix: boolean = true, tokenValue: string = '', tokenAdjustmentValue?: string): string {
        // Return branded app text, based on partial intl.messages path token suffix. Auto concat'ed with this.messagesPathPrefix
        let result = '';
        if (!messagePath || !this.messagesPathPrefix || !this.props.intl || !this.props.intl.messages) { return result; }
        const path = prependMessagesPathPrefix ? `${this.messagesPathPrefix}${messagePath}` : messagePath;

        result = _.get(this.props.intl.messages, path);

        if (result === undefined) {
            // console.log(`ERROR: AppComponent.t: Undefined message for token path "${path}".`);
            result = '';
        }

        const re = /{TOKEN_VALUE}/gi;
        const rep = /{TOKEN_ADJUSTMENT_VALUE}/gi;
        result = result.replace(re, tokenValue);
        if (tokenAdjustmentValue) { result = result.replace(rep, tokenAdjustmentValue); }

        return result;
    }

    public appTextCommon(messagePath: string, prependMessagePath: string = 'app.common.', applyPrependPath: boolean = false, tokenValue: string = ''): string {
        // Return branded app text, based on partial intl.me~ssages path token suffix. Auto concat'ed with this.messagesPathPrefix
        const { intl } = this.props;
        if (!messagePath || !intl || !intl.messages) { return ''; }

        const path = `${prependMessagePath}${messagePath}`;

        return this.appText(path, applyPrependPath, tokenValue);
    }

    // Default prepend these, they change per page
    public appTextGridColumnHeader(messagePath: string, gridHeaderPathPrefix: string = this.commonMessagePaths.gridColumnHeaders, prependPath: boolean = true): string {
        // Return grid column header (shorthand for {MESSAGESPATHPREFIX}.grid.column-headers.{PATH})
        const path = `${gridHeaderPathPrefix}${messagePath}`;

        return this.appText(path, prependPath);
    }

    public appTextPageTitle(messagePath: string = this.commonMessagePaths.pageTitle, prependPath: boolean = true): string {
        // Returns the page title
        return this.appText(messagePath, prependPath);
    }

    // Default do not prepend, these come from common pool
    public appTextFilterOptionAny(messagePath: string = this.commonMessagePaths.filterOptionAny, prependPath: boolean = false): string {
        // Returns the filter option any
        return this.appText(messagePath, prependPath);
    }

    public appTextResults(messagePath: string = this.commonMessagePaths.searchResult, prependPath: boolean = false): string {
        // Returns the search results
        return this.appText(messagePath, prependPath);
    }

    public appTextPlural(messagePath: string, itemCount: number, prependItemCount: boolean = true, prependPath: boolean = false): string {
        // Returns the search results
        let result = this.appText(messagePath, prependPath);

        return Formatters.plural(result, itemCount, prependItemCount);
    }

    public appTextResultsAndPageTitle(
        resultsCount: number = 0,
        // messagePathResults: string = this.commonMessagePaths.searchResults,
        messagePathResults: string = this.commonMessagePaths.searchResult,
        prependResultsPath: boolean = false,
        messagePathPageTitle: string = this.commonMessagePaths.pageTitle,
        prependPageTitlePath: boolean = true): string {
        // Returns a caption used for WhiteBoxPageSection title values for search results pages
        const pageTitle = this.appTextPageTitle(messagePathPageTitle, prependPageTitlePath);
        const results = this.appTextResults(messagePathResults, prependResultsPath);
        const resText = this.appTextPlural(messagePathResults, resultsCount, true, prependResultsPath);

        const res = `${pageTitle} (${resText})`;

        // return `${pageTitle} (${resultsCount} ${results})`;

        return res;
    }

    public appTextPageTitleAppendString(appendString: any = '', messagePathPageTitle: string = this.commonMessagePaths.pageTitle, prependPageTitlePath: boolean = true): string {
        // Returns a caption used for page titles like (TITLE: ASDF)
        const pageTitle = this.appTextPageTitle(messagePathPageTitle, prependPageTitlePath);
        const append = appendString ? `: ${appendString}` : '';

        return `${pageTitle}${append}`;
    }

    public appTextSelectOptionPlaceholder(messagePath: string = this.commonMessagePaths.selectOptionPlaceholder, prependPath: boolean = false): string {
        // Returns the select option placeholder
        return this.appText(messagePath, prependPath);
    }

    public appTextDateInputPlaceholder(messagePath: string = this.commonMessagePaths.dateInputPlaceholder, prependPath: boolean = false): string {
        return this.appText(messagePath, prependPath);
    }

    public appTextNumberInputPlaceholder(messagePath: string = this.commonMessagePaths.numberInputPlaceholder, prependPath: boolean = false): string {
        return this.appText(messagePath, prependPath);
    }

    public enforceAdminOnly(): boolean {
        const auth = new Auth();
        const isAdmin = auth.isAdmin();
        const currentUser = auth.getSessionUsername();

        let enforce = (isAdmin) ? false : true;

        // console.log(`AppComponent:enforceAdminOnly enforce ${enforce} currentUser ${currentUser} isAdmin ${isAdmin}`);

        return enforce;
    }

    public enforceAdminOrEmployeeOnly(): boolean {
        const auth = new Auth();
        const isAdmin = auth.isAdmin();
        const isEmployee = auth.isEmployee();

        let enforce = (isAdmin || isEmployee) ? false : true;

        return enforce;
    }

    public enforceAdminOrMatchedUsername(matchUsername: string = ''): boolean {
        const auth = new Auth();
        const isAdmin = auth.isAdmin();
        const currentUser = auth.getSessionUsername();

        let enforce = false;

        if (!isAdmin && matchUsername && (currentUser !== matchUsername)) {
            enforce = true;
        }

        // console.log(`AppComponent:enforceAdminOrMatchedUsername enforce ${enforce} currentUser ${currentUser} matchUsername ${matchUsername} isAdmin ${isAdmin}`);

        return enforce;
    }

    public enforceAdminOrMatchedUserObjId(matchUserObjId: string = ''): boolean {
        const auth = new Auth();
        const isAdmin = auth.isAdmin();
        const currentUserObjId = auth.getSessionUserObjectId();

        let enforce = false;

        if (!isAdmin && matchUserObjId && (currentUserObjId !== matchUserObjId)) {
            enforce = true;
        }

        // console.log(`AppComponent:enforceAdminOrMatchedUsername enforce ${enforce} currentUser ${currentUser} matchUsername ${matchUsername} isAdmin ${isAdmin}`);

        return enforce;
    }
}

export abstract class AppComponentNoWrapper<TProps extends IAppComponentProps = IAppComponentProps, TState extends IAppComponentState = IAppComponentState> extends React.Component<TProps, TState> {
    public messagesPathPrefix: string = '';

    public readonly commonMessagePaths = {
        dateInputPlaceholder: 'app.common.forms.date-input.placeholder',
        filterOptionAny: 'app.common.forms.filter-option-any',
        gridColumnHeaders: 'grid.column-headers.',
        numberInputPlaceholder: 'app.common.forms.number-input.placeholder',
        pageTitle: 'page-title',
        searchResults: 'app.common.search.results',
        searchResult: 'app.common.search.result',
        selectOptionPlaceholder: 'app.common.forms.select-option.placeholder',
    };

    public constructor(p: TProps, messagesPathPrefix: string = '') {
        super(p);

        if (messagesPathPrefix) { this.messagesPathPrefix = messagesPathPrefix; }

        // console.log(`AppComponentNoWrapper constructor messagepath ${messagesPathPrefix} props`, p);
    }

    /* Children cloning & copy props */
    public cloneChildrenWithProps = (children, props) => {
        return (React.cloneElement(children as React.ReactElement<any>, { ...props }));
    }

    /* Page rendering */
    public renderPage(content: any) {
        // console.log('AppComponentNoWrapper 1 renderPage props', this.props);

        return (
            { content }
        );
    }

    /* Branding/Internationalization/App Text functionality */
    public appText(messagePath: string, prependMessagesPathPrefix: boolean = true, tokenValue: string = ''): string {
        // Return branded app text, based on partial intl.messages path token suffix. Auto concat'ed with this.messagesPathPrefix
        let result = '';
        if (!messagePath || !this.messagesPathPrefix || !this.props.intl || !this.props.intl.messages) { return result; }
        const path = prependMessagesPathPrefix ? `${this.messagesPathPrefix}${messagePath}` : messagePath;

        result = _.get(this.props.intl.messages, path);

        if (result === undefined) {
            // console.log(`ERROR: AppComponentNoWrapper.t: Undefined message for token path "${path}".`);
            result = '';
        }

        const re = /{TOKEN_VALUE}/gi;
        result = result.replace(re, tokenValue);

        return result;
    }

    // Default prepend these, they change per page
    public appTextGridColumnHeader(messagePath: string, gridHeaderPathPrefix: string = this.commonMessagePaths.gridColumnHeaders, prependPath: boolean = true): string {
        // Return grid column header (shorthand for {MESSAGESPATHPREFIX}.grid.column-headers.{PATH})
        const path = `${gridHeaderPathPrefix}${messagePath}`;

        return this.appText(path, prependPath);
    }

    public appTextPageTitle(messagePath: string = this.commonMessagePaths.pageTitle, prependPath: boolean = true): string {
        // Returns the page title
        return this.appText(messagePath, prependPath);
    }

    // Default do not prepend, these come from common pool
    public appTextFilterOptionAny(messagePath: string = this.commonMessagePaths.filterOptionAny, prependPath: boolean = false): string {
        // Returns the filter option any
        return this.appText(messagePath, prependPath);
    }

    public appTextResults(messagePath: string = this.commonMessagePaths.searchResult, prependPath: boolean = false): string {
        // Returns the search results
        return this.appText(messagePath, prependPath);
    }

    public appTextResultsAndPageTitle(
        resultsCount: number = 0,
        messagePathResults: string = this.commonMessagePaths.searchResult,
        prependResultsPath: boolean = false,
        messagePathPageTitle: string = this.commonMessagePaths.pageTitle,
        prependPageTitlePath: boolean = true): string {
        // Returns a caption used for WhiteBoxPageSection title values for search results pages
        const pageTitle = this.appTextPageTitle(messagePathPageTitle, prependPageTitlePath);
        const results = this.appTextResults(messagePathResults, prependResultsPath);

        return `${pageTitle} (${resultsCount} ${results})`;
    }

    public appTextPageTitleAppendString(appendString: any = '', messagePathPageTitle: string = this.commonMessagePaths.pageTitle, prependPageTitlePath: boolean = true): string {
        // Returns a caption used for page titles like (TITLE: ASDF)
        const pageTitle = this.appTextPageTitle(messagePathPageTitle, prependPageTitlePath);
        const append = appendString ? `: ${appendString}` : '';

        return `${pageTitle}${append}`;
    }
}



// WEBPACK FOOTER //
// ./src/common/app-component-base.tsx